import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type { ButtonEntity, ButtonItem, LegacyButtonDefault, LegacyButtonZero } from '@sites/entity-core';
import { buttonLegacyDefaultV0Schema, buttonLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { animationPlugin } from '../../plugins/widget/animation.plugin';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type ButtonFormModel = ButtonEntity;
export type ButtonDataModel = ButtonEntity['data'];
export type { ButtonItem };

/**
 * Button-specific accessors shared verbatim by both variant assemblers — the
 * buttons array (with its immutable array sugar) and the group-layout fields.
 * These are structurally identical on both variants (only `position` differs,
 * and that comes from the position plugins), so the lazy `raw` thunk is
 * narrowed to the default variant's FieldTree internally.
 *
 * Exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model) — composed with {@link composeForm},
 * never object spread.
 */
function buttonSharedAccessors(raw: () => FieldTree<LegacyButtonDefault> | FieldTree<LegacyButtonZero>) {
    const r = raw as () => FieldTree<LegacyButtonDefault>;
    return {
        // Buttons array — exposed as a single FieldTree branch plus immutable
        // array sugar. Per-item field access goes through `buttonAt(i)` which
        // resolves the indexed child FieldTree (`form.buttonAt(0).label`).
        get buttons() {
            return r().data.buttons;
        },
        /**
         * Current buttons array snapshot as a computed signal — template-friendly
         * sugar over `buttons().value()` (empty array before the model is seeded).
         */
        buttonItems: computed<ButtonItem[]>(() => r().data.buttons?.().value() ?? []),
        setButtons: (value: ButtonItem[]) => {
            r().data.buttons().value.set(value);
        },
        buttonAt: (index: number): FieldTree<ButtonItem> | undefined => {
            return (r().data.buttons as unknown as Record<number, FieldTree<ButtonItem>>)[index];
        },
        addButton: (item: ButtonItem) => {
            r()
                .data.buttons()
                .value.update((arr) => [...arr, item]);
        },
        removeButton: (index: number) => {
            r()
                .data.buttons()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveButton: (from: number, to: number) => {
            r()
                .data.buttons()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateButton: (index: number, patch: Partial<ButtonItem>) => {
            r()
                .data.buttons()
                .value.update((arr) => arr.map((b, i) => (i === index ? { ...b, ...patch } : b)));
        },

        // Group layout
        get horizontalIndent() {
            return r().data.horizontalIndent;
        },
        setHorizontalIndent: (value: ButtonDataModel['horizontalIndent']) => {
            r().data.horizontalIndent().value.set(value);
        },
        get horizontalAlign() {
            return r().data.horizontalAlign;
        },
        setHorizontalAlign: (value: ButtonDataModel['horizontalAlign']) => {
            r().data.horizontalAlign().value.set(value);
        },
        get mobileHorizontalAlign() {
            return r().data.mobileHorizontalAlign;
        },
        setMobileHorizontalAlign: (value: ButtonDataModel['mobileHorizontalAlign']) => {
            r().data.mobileHorizontalAlign().value.set(value);
        },
        get tabletHorizontalAlign() {
            return r().data.tabletHorizontalAlign;
        },
        setTabletHorizontalAlign: (value: ButtonDataModel['tabletHorizontalAlign']) => {
            r().data.tabletHorizontalAlign().value.set(value);
        },
        get isSynchronizeStyle() {
            return r().data.isSynchronizeStyle;
        },
        /**
         * Style-synchronisation flag with the legacy-null rule applied: very
         * old widgets persisted `null`, which the FormGroup editor treated as
         * "off" — truthy check on purpose.
         */
        isSynchronized: computed<boolean>(() => Boolean(r().data.isSynchronizeStyle?.().value())),
        setIsSynchronizeStyle: (value: boolean) => {
            r().data.isSynchronizeStyle().value.set(value);
        },
    };
}

/**
 * Per-variant accessor assemblers. Unlike video (where the variants diverge
 * on several layout fields) the two button variants share every accessor and
 * differ **only** in the `position` branch type: the default form's grid is
 * the simple per-device `WidgetPosition` (via {@link widgetPositionPlugin}),
 * the zero form's is the full `ExtendedPosition` (anchors, heights,
 * changed-flags — via {@link extendedPositionPlugin}).
 *
 * The first `composeForm` part widens the inherited base methods
 * (`syncFromEntity`, `setValue`, `value`) to operate on `ButtonEntity` so the
 * dispatcher's union return collapses into a single useful shape for callers
 * that only know the union (e.g. `WebLegacyWidgetSignalFormService`).
 */
function assembleDefaultButtonForm(base: BaseLegacyEntityForm<LegacyButtonDefault>) {
    // `_raw` stays typed as `FieldTree<LegacyButtonDefault>` so each accessor
    // resolves to the correct nested `FieldTree<…>`.
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<ButtonEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        buttonSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroButtonForm(base: BaseLegacyEntityForm<LegacyButtonZero>) {
    // See {@link assembleDefaultButtonForm} — `_raw` is the narrow
    // `FieldTree<LegacyButtonZero>`, which gives `position` its correct
    // `FieldTree<ExtendedPosition>` type.
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<ButtonEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        buttonSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** button widget. Validates against
 * the legacy default envelope schema, which permits an absent `type` field for
 * parity with pre-discriminator persisted data on disk.
 *
 * **Field accessors are FieldTree references** — exposed via getters so the
 * FieldTree paths materialise lazily (after `syncFromEntity` populates the
 * model). Consumers call them to get the `FieldState`, then read `.value()`,
 * `.touched()`, `.dirty()`, `.errors()` as needed. Convenience `setX(v)`
 * methods are sugar over `field().value.set(v)`; the `buttons` array adds
 * `addButton` / `removeButton` / `moveButton` / `updateButton` /
 * `buttonAt(i)` sugar on top.
 *
 * ```ts
 * form.buttons().value()          // current ButtonItem[]
 * form.buttonAt(0)?.label()       // FieldState of the first button's label
 * form.updateButton(0, { label }) // immutable per-item patch
 * ```
 */
export function createLegacyButtonDefaultForm(options?: BaseLegacyEntityFormOptions<LegacyButtonDefault>) {
    const base = createBaseLegacyEntityForm<LegacyButtonDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, buttonLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // The assembler takes the narrow `BaseLegacyEntityForm<LegacyButtonDefault>`
    // so its internal `_raw` keeps the precise FieldTree shape; the public
    // widening to `ButtonEntity` happens inside the assembler, via a checked
    // assignment on the base part.
    return assembleDefaultButtonForm(base);
}

/**
 * Signal-form view of a **zero-block-placement** button widget. Validates
 * against the legacy zero envelope schema, which requires `type: 'zero'` on
 * the envelope (the host-app factory copies `meta.type` onto the entity at
 * creation time).
 *
 * Diverges from {@link createLegacyButtonDefaultForm} only on `position`:
 * the zero form's grid is the full `ExtendedPosition` (per-device
 * width/height/x/y/rotate/zIndex plus anchor sides) instead of the default
 * variant's simple `WidgetPosition`.
 */
export function createLegacyButtonZeroForm(options?: BaseLegacyEntityFormOptions<LegacyButtonZero>) {
    const base = createBaseLegacyEntityForm<LegacyButtonZero>((schemaPath) => {
        validateStandardSchema(schemaPath, buttonLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // See `createLegacyButtonDefaultForm` — narrow base in, widening happens
    // inside the assembler.
    return assembleZeroButtonForm(base);
}

/**
 * Variant dispatcher. Callers that only know `ButtonEntity` (the union of
 * default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and the
 * dispatcher reads the variant tag from `options.initialData?.type` to pick
 * the matching per-variant factory.
 *
 * The return type is the **union** of the two variant forms. Consumers that
 * want to read variant-specific accessors (the `position` branch types
 * diverge) must narrow via `form.value().type === 'zero'` first, or use the
 * variant factories directly to skip narrowing.
 */
export function createButtonForm(options?: BaseLegacyEntityFormOptions<ButtonEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyButtonZeroForm(options as BaseLegacyEntityFormOptions<LegacyButtonZero>);
    }

    return createLegacyButtonDefaultForm(options as BaseLegacyEntityFormOptions<LegacyButtonDefault>);
}

export type ButtonForm = ReturnType<typeof createButtonForm>;
export type ButtonDefaultForm = ReturnType<typeof createLegacyButtonDefaultForm>;
export type ButtonZeroForm = ReturnType<typeof createLegacyButtonZeroForm>;
